// CreateTicketModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Box,
  Stack,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  OutlinedInput,
  InputAdornment,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  AttachFile as AttachFileIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

// Mock data for dropdowns
const ticketTypes = [
  { value: 'task', label: 'Task', icon: <CheckCircleIcon /> },
  { value: 'bug', label: 'Bug', icon: <CheckCircleIcon color="error" /> },
  { value: 'story', label: 'Story', icon: <CheckCircleIcon color="primary" /> },
  { value: 'subtask', label: 'Subtask', icon: <CheckCircleIcon color="secondary" /> }
];

const priorities = [
  { value: 'low', label: 'Low', color: 'success' },
  { value: 'medium', label: 'Medium', color: 'warning' },
  { value: 'high', label: 'High', color: 'error' },
  { value: 'critical', label: 'Critical', color: 'error' }
];

const teams = [
  { value: 'frontend', label: 'Frontend Team' },
  { value: 'backend', label: 'Backend Team' },
  { value: 'design', label: 'Design Team' },
  { value: 'qa', label: 'QA Team' }
];

const labels = [
  { value: 'bug', label: 'Bug', color: '#e11d48' },
  { value: 'feature', label: 'Feature', color: '#2563eb' },
  { value: 'urgent', label: 'Urgent', color: '#ea580c' },
  { value: 'documentation', label: 'Documentation', color: '#0d9488' },
  { value: 'enhancement', label: 'Enhancement', color: '#7c3aed' }
];

const sprints = [
  { value: 'sprint-1', label: 'Sprint 1' },
  { value: 'sprint-2', label: 'Sprint 2' },
  { value: 'sprint-3', label: 'Sprint 3' }
];

const epics = [
  { value: 'epic-1', label: 'User Authentication' },
  { value: 'epic-2', label: 'Payment System' },
  { value: 'epic-3', label: 'Dashboard Redesign' }
];

const users = [
  { value: 'user-1', label: 'Alex Johnson', avatar: 'AJ' },
  { value: 'user-2', label: 'Taylor Smith', avatar: 'TS' },
  { value: 'user-3', label: 'Jordan Lee', avatar: 'JL' },
  { value: 'user-4', label: 'Morgan Chen', avatar: 'MC' }
];

const tickets = [
  { value: 'ticket-1', label: 'Fix login bug' },
  { value: 'ticket-2', label: 'Implement dark mode' },
  { value: 'ticket-3', label: 'Update API documentation' },
  { value: 'ticket-4', label: 'Optimize database queries' }
];

const CreateTicketModal = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const summaryRef = useRef();

  // Form state
  const [formData, setFormData] = useState({
    ticketType: 'task',
    summary: '',
    description: '',
    checklist: [{ text: '', completed: false }],
    reporter: users[0], // Default to first user
    assignee: '',
    team: '',
    labels: [],
    priority: 'medium',
    estimatedTime: '',
    dueDateTime: null,
    sprint: 'sprint-1',
    epic: '',
    dependsOn: [],
    plannedDateTime: null,
    attachments: [],
    addMore: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Focus summary field on open
  useEffect(() => {
    if (open && summaryRef.current) {
      setTimeout(() => summaryRef.current.focus(), 100);
    }
  }, [open]);

  // Handle form input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle checklist changes
  const handleChecklistChange = (index, field, value) => {
    const newChecklist = [...formData.checklist];
    newChecklist[index] = { ...newChecklist[index], [field]: value };
    handleChange('checklist', newChecklist);
  };

  // Add new checklist item
  const addChecklistItem = () => {
    handleChange('checklist', [...formData.checklist, { text: '', completed: false }]);
  };

  // Remove checklist item
  const removeChecklistItem = (index) => {
    if (formData.checklist.length <= 1) return;
    const newChecklist = [...formData.checklist];
    newChecklist.splice(index, 1);
    handleChange('checklist', newChecklist);
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
      file
    }));
    handleChange('attachments', [...formData.attachments, ...newAttachments]);
  };

  // Remove attachment
  const removeAttachment = (id) => {
    handleChange('attachments', formData.attachments.filter(att => att.id !== id));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.ticketType) {
      newErrors.ticketType = 'Ticket type is required';
    }
    if (!formData.summary.trim()) {
      newErrors.summary = 'Summary is required';
    }
    // Removed assignee and reporter validation as they're not required by backend
    
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async () => {
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Call the actual API through the onSave callback
      await onSave(formData);
      
      if (!formData.addMore) {
        onClose();
      } else {
        // Reset form except defaults
        setFormData(prev => ({
          ...prev,
          summary: '',
          description: '',
          checklist: [{ text: '', completed: false }],
          assignee: '',
          team: '',
          labels: [],
          estimatedTime: '',
          dueDateTime: null,
          epic: '',
          dependsOn: [],
          plannedDateTime: null,
          attachments: []
        }));
      }
    } catch (error) {
      // Error handling is done in the parent component
      // The error will be re-thrown to be handled by the parent
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle key events
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSubmit();
    }
  };

  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
      onKeyDown={handleKeyDown}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Create New Ticket</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* Ticket Type */}
            <FormControl fullWidth error={!!errors.ticketType}>
              <InputLabel id="ticket-type-label">Ticket Type *</InputLabel>
              <Select
                labelId="ticket-type-label"
                value={formData.ticketType}
                label="Ticket Type *"
                onChange={(e) => handleChange('ticketType', e.target.value)}
              >
                {ticketTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {type.icon}
                      <span>{type.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              {errors.ticketType && (
                <Typography color="error" variant="caption">
                  {errors.ticketType}
                </Typography>
              )}
            </FormControl>
            
            {/* Summary */}
            <FormControl fullWidth error={!!errors.summary}>
              <TextField
                inputRef={summaryRef}
                label="Summary *"
                placeholder="Write a short summary of the issue"
                value={formData.summary}
                onChange={(e) => handleChange('summary', e.target.value)}
                error={!!errors.summary}
                helperText={errors.summary || `${formData.summary.length}/200 characters`}
                inputProps={{ maxLength: 200 }}
                autoFocus
              />
            </FormControl>
            
            {/* Description */}
            <TextField
              label="Description"
              placeholder="Add context, acceptance criteria, steps to reproduce…"
              multiline
              rows={4}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
            />
            
            {/* Checklist */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1">Checklist</Typography>
                <Button 
                  startIcon={<AddIcon />} 
                  onClick={addChecklistItem}
                  size="small"
                >
                  Add Item
                </Button>
              </Stack>
              
              <Stack spacing={1}>
                {formData.checklist.map((item, index) => (
                  <Stack 
                    key={index} 
                    direction="row" 
                    alignItems="center" 
                    spacing={1}
                  >
                    <Checkbox
                      checked={item.completed}
                      onChange={(e) => handleChecklistChange(index, 'completed', e.target.checked)}
                    />
                    <TextField
                      fullWidth
                      placeholder="Checklist item"
                      value={item.text}
                      onChange={(e) => handleChecklistChange(index, 'text', e.target.value)}
                      size="small"
                    />
                    {formData.checklist.length > 1 && (
                      <IconButton 
                        size="small" 
                        onClick={() => removeChecklistItem(index)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Box>
            
            <Divider />
            
            {/* Reporter */}
            <FormControl fullWidth error={!!errors.reporter}>
              <InputLabel id="reporter-label">Reporter</InputLabel>
              <Select
                labelId="reporter-label"
                value={formData.reporter.value}
                label="Reporter"
                onChange={(e) => handleChange('reporter', users.find(u => u.value === e.target.value))}
              >
                {users.map(user => (
                  <MenuItem key={user.value} value={user.value}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                        {user.avatar}
                      </Avatar>
                      <span>{user.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              {errors.reporter && (
                <Typography color="error" variant="caption">
                  {errors.reporter}
                </Typography>
              )}
            </FormControl>
            
            {/* Assignee */}
            <FormControl fullWidth error={!!errors.assignee}>
              <InputLabel id="assignee-label">Assignee</InputLabel>
              <Select
                labelId="assignee-label"
                value={formData.assignee}
                label="Assignee"
                onChange={(e) => handleChange('assignee', e.target.value)}
                startAdornment={
                  formData.assignee && (
                    <InputAdornment position="start">
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                        {users.find(u => u.value === formData.assignee)?.avatar}
                      </Avatar>
                    </InputAdornment>
                  )
                }
              >
                <MenuItem value="">
                  <em>Select assignee</em>
                </MenuItem>
                <MenuItem value="auto">Auto Assign</MenuItem>
                {users.map(user => (
                  <MenuItem key={user.value} value={user.value}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                        {user.avatar}
                      </Avatar>
                      <span>{user.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              {errors.assignee && (
                <Typography color="error" variant="caption">
                  {errors.assignee}
                </Typography>
              )}
              <Button 
                size="small" 
                sx={{ mt: 1, width: 'fit-content' }}
                onClick={() => handleChange('assignee', 'user-1')}
              >
                Assign To Me
              </Button>
            </FormControl>
            
            {/* Team */}
            <FormControl fullWidth>
              <InputLabel id="team-label">Team</InputLabel>
              <Select
                labelId="team-label"
                value={formData.team}
                label="Team"
                onChange={(e) => handleChange('team', e.target.value)}
              >
                <MenuItem value=""><em>Select Team</em></MenuItem>
                {teams.map(team => (
                  <MenuItem key={team.value} value={team.value}>
                    {team.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Labels */}
            <FormControl fullWidth>
              <InputLabel id="labels-label">Labels</InputLabel>
              <Select
                labelId="labels-label"
                multiple
                value={formData.labels}
                onChange={(e) => handleChange('labels', e.target.value)}
                input={<OutlinedInput label="Labels" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const label = labels.find(l => l.value === value);
                      return (
                        <Chip
                          key={value}
                          label={label?.label}
                          size="small"
                          sx={{ 
                            height: 20,
                            bgcolor: label?.color,
                            color: 'white',
                            '& .MuiChip-label': { px: 0.5 }
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {labels.map(label => (
                  <MenuItem key={label.value} value={label.value}>
                    <Checkbox checked={formData.labels.indexOf(label.value) > -1} />
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: label.color,
                        mr: 1
                      }}
                    />
                    {label.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Priority */}
            <FormControl fullWidth>
              <InputLabel id="priority-label">Priority *</InputLabel>
              <Select
                labelId="priority-label"
                value={formData.priority}
                label="Priority *"
                onChange={(e) => handleChange('priority', e.target.value)}
              >
                {priorities.map(priority => (
                  <MenuItem key={priority.value} value={priority.value}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: `${priority.color}.main`
                        }}
                      />
                      <span>{priority.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Estimated Time */}
            <TextField
              label="Estimated Time"
              placeholder="3w 4d 12h 30m"
              value={formData.estimatedTime}
              onChange={(e) => handleChange('estimatedTime', e.target.value)}
              helperText="Format: 3w 4d 12h 30m"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccessTimeIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Due Date & Time */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Due Date & Time"
                value={formData.dueDateTime}
                onChange={(datetime) => handleChange('dueDateTime', datetime)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    placeholder: "Select due date and time",
                    helperText: 'Set deadline for this ticket',
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    },
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      },
                    }
                  },
                  popper: {
                    sx: {
                      '& .MuiPaper-root': {
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        borderRadius: 2,
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            
            {/* Sprint */}
            <FormControl fullWidth>
              <InputLabel id="sprint-label">Sprint</InputLabel>
              <Select
                labelId="sprint-label"
                value={formData.sprint}
                label="Sprint"
                onChange={(e) => handleChange('sprint', e.target.value)}
              >
                <MenuItem value="backlog">Backlog</MenuItem>
                {sprints.map(sprint => (
                  <MenuItem key={sprint.value} value={sprint.value}>
                    {sprint.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Epic */}
            <FormControl fullWidth>
              <InputLabel id="epic-label">Epic</InputLabel>
              <Select
                labelId="epic-label"
                value={formData.epic}
                label="Epic"
                onChange={(e) => handleChange('epic', e.target.value)}
              >
                <MenuItem value=""><em>Select Epic</em></MenuItem>
                {epics.map(epic => (
                  <MenuItem key={epic.value} value={epic.value}>
                    {epic.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Depends On */}
            <FormControl fullWidth>
              <InputLabel id="depends-label">Depends On</InputLabel>
              <Select
                labelId="depends-label"
                multiple
                value={formData.dependsOn}
                onChange={(e) => handleChange('dependsOn', e.target.value)}
                input={<OutlinedInput label="Depends On" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const ticket = tickets.find(t => t.value === value);
                      return (
                        <Chip
                          key={value}
                          label={ticket?.label}
                          size="small"
                          onDelete={() => handleChange(
                            'dependsOn', 
                            formData.dependsOn.filter(id => id !== value)
                          )}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {tickets.map(ticket => (
                  <MenuItem key={ticket.value} value={ticket.value}>
                    <Checkbox checked={formData.dependsOn.indexOf(ticket.value) > -1} />
                    {ticket.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Planned Date & Time */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Planned Date & Time"
                value={formData.plannedDateTime}
                onChange={(datetime) => handleChange('plannedDateTime', datetime)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    placeholder: "Select planned start date and time",
                    helperText: 'Set when work should begin',
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    },
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      },
                    }
                  },
                  popper: {
                    sx: {
                      '& .MuiPaper-root': {
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        borderRadius: 2,
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            
            {/* Attachments */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>Attachments</Typography>
              
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
                onClick={() => document.getElementById('file-upload').click()}
              >
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <Stack alignItems="center" spacing={1}>
                  <AttachFileIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Drag & drop files here or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Supports images, PDFs, DOCX. Max 25MB
                  </Typography>
                </Stack>
              </Box>
              
              {formData.attachments.length > 0 && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  {formData.attachments.map(att => (
                    <Stack 
                      key={att.id} 
                      direction="row" 
                      alignItems="center" 
                      spacing={1}
                      sx={{ 
                        p: 1, 
                        bgcolor: 'action.hover', 
                        borderRadius: 1 
                      }}
                    >
                      <AttachFileIcon fontSize="small" />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">{att.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {att.size}
                        </Typography>
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={() => removeAttachment(att.id)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.addMore}
              onChange={(e) => handleChange('addMore', e.target.checked)}
            />
          }
          label="Add more"
        />
        
        <Box sx={{ flexGrow: 1 }} />
        
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={() => {
            handleSubmit();
          }}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTicketModal;
